import { getStringArrayValue } from '@intelligence/v3/filterControls/shared/valueUtils';
import { buildRobloxGenreFilterParam } from '@intelligence/v3/utils/buildRobloxGenreFilter';
import { appendRobloxFetchDateParams } from '@intelligence/v3/utils/robloxFilterDate';

import {
  getDefaultFilterValues,
  ROBLOX_GAME_RANK_FIXED_FETCH_PARAMS,
  ROBLOX_RANK_DEFAULT_PAGE_SIZE,
  getRobloxGameRankSortMetricByKey,
  mapRankSortUiOrderToApiOrder,
} from '../constants';
import { ROBLOX_GAME_RANK_DEFAULT_SOURCE } from '../config/robloxRankSourceConfig';

import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxGameRankFilterValues } from '../types';
import type { IRobloxGenreFilterItem } from '@intelligence/v3/utils/buildRobloxGenreFilter';

export interface IRobloxGameRankFetchParams {
  start_date?: string;
  end_date?: string;
  group_by: string[];
  use_entity_type: string;
  use_custom_fields: string[];
  markets: string[];
  devices: string[];
  page: number;
  page_size: number;
  order_by: string[];
  sources: string[];
  filter: {
    roblox_genre?: IRobloxGenreFilterItem[];
  };
  keyword?: string;
  game_ids?: string[];
  rank_game_ids?: string[];
}

interface IBuildRobloxGameRankFetchParamsOptions {
  page?: number;
  pageSize?: number;
  keyword?: string;
  gameIds?: string[];
  /** 搜索后点击结果时使用，与 gameIds 互斥 */
  rankGameIds?: string[];
  source?: string;
  rankOrder?: 'asc' | 'desc';
  rankSortKey?: string;
  robloxGenreOptions?: IMainGenre[];
}

const buildRobloxGameRankOrderBy = (
  sortMetric: 'rank' | 'rank_dod',
  uiOrder: 'asc' | 'desc',
): string => {
  if (sortMetric === 'rank') {
    return `rank ${mapRankSortUiOrderToApiOrder(uiOrder)}`;
  }

  return `rank_dod ${uiOrder}`;
};

export const buildRobloxGameRankFetchParams = (
  filterValues: IRobloxGameRankFilterValues,
  options?: IBuildRobloxGameRankFetchParamsOptions,
): IRobloxGameRankFetchParams => {
  const defaults = getDefaultFilterValues();
  const keyword = options?.keyword?.trim();
  const gameIds = options?.gameIds?.filter(Boolean);
  const rankGameIds = options?.rankGameIds?.filter(Boolean);
  const uiRankOrder = options?.rankOrder || 'desc';
  const rankSortMetric = getRobloxGameRankSortMetricByKey(options?.rankSortKey);
  const genreFilter = buildRobloxGenreFilterParam(
    filterValues.genre ?? defaults.genre,
    options?.robloxGenreOptions,
  );

  const params: IRobloxGameRankFetchParams = appendRobloxFetchDateParams({
    group_by: [...ROBLOX_GAME_RANK_FIXED_FETCH_PARAMS.group_by],
    use_entity_type: ROBLOX_GAME_RANK_FIXED_FETCH_PARAMS.use_entity_type,
    use_custom_fields: [...ROBLOX_GAME_RANK_FIXED_FETCH_PARAMS.use_custom_fields],
    order_by: ['date asc', buildRobloxGameRankOrderBy(rankSortMetric, uiRankOrder)],
    sources: [options?.source || ROBLOX_GAME_RANK_DEFAULT_SOURCE],
    markets: getStringArrayValue(filterValues.region ?? defaults.region),
    devices: getStringArrayValue(filterValues.device ?? defaults.device),
    page: options?.page ?? 1,
    page_size: options?.pageSize ?? ROBLOX_RANK_DEFAULT_PAGE_SIZE,
    filter: genreFilter,
  }, filterValues);

  if (keyword) {
    params.keyword = keyword;
  }

  if (rankGameIds && rankGameIds.length > 0) {
    params.rank_game_ids = rankGameIds;
  } else if (gameIds && gameIds.length > 0) {
    params.game_ids = gameIds;
  }

  return params;
};
